/* eslint-disable react/no-array-index-key */

import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Row, Col } from 'reactstrap';
import { Field } from '@cordata/buffet';

import DefaultValueField from './filters/DefaultValueField';

const filterTypeOptions = [
  'custom dropdown',
  'checkbox',
  'boolean radio buttons',
  'date range',
  'badge',
  'provider',
  'facility',
  'user',
  'number',
  'string',
  'other reference data',
].map(label => ({
  label,
  value: label,
}));

const numberFilterComparisonTypes = [{
  // values in this are the operators used by the python api
  // see query_utils.py
  label: 'Equal to',
  value: 'eq',
  text: 'Will match any value that exactly equals the number the user enters',
}, {
  label: 'Not equal to',
  value: '!',
  text: 'Will match any value not equal to what the user enters',
}, {
  label: 'Less than',
  value: '<',
  text: 'Will match any value less than what the user enters',
}, {
  label: 'Less than or equal to',
  value: '<<',
  text: 'Will match any value less than or equal to what the user enters',
}, {
  label: 'Greater than',
  value: '>',
  text: 'Will match any value greater than what the user enters',
}, {
  label: 'Greater than or equal to',
  value: '>>',
  text: 'Will match any value greater than or equal to what the user enters',
}];

const FilterForm = ({
  index,
  availableRefDataModels,
}) => {
  const {
    values,
    setFieldValue,
    setFieldError,
    setFieldTouched,
  } = useFormikContext();

  useEffect(() => {
    const { reportView } = values;

    if (reportView) {
      // if the user imports an exported configuration, and any of the
      // columns in the report view have had their names changed, the report
      // will fail to save because of the discrepancy. show an error to the
      // user to alert them to this
      if (values.filters[index].columnName) {
        if (!reportView.columns.some(c => c.value === values.filters[index].columnName.value)) {
          const field = `filters.${index}.columnName`;
          const errorMessage = `A column named "${values.filters[index].columnName.value}" is not present in ${reportView.value}. Please re-select the correct column.`;

          setFieldError(field, errorMessage);
          setFieldTouched(field, true);
        }
      }
    }
  }, [values.reportView, index]);

  useEffect(() => {
    if (values.filters[index].type) {
      if (values.filters[index].type.value === 'badge') {
        setFieldValue(`filters.${index}.isMultiSelect`, true);
      } else if (values.filters[index].type.value === 'checkbox') {
        setFieldValue(`filters.${index}.isMultiSelect`, false);
      }

      if (values.filters[index].type.value === 'custom dropdown') {
        setFieldValue(`filters.${index}.customDropdownOptions`, values.filters[index].customDropdownOptions || []);
      }
    }
  }, [values.filters[index].type]);

  return (<>
    <Row form>
      <Col sm>
        <Field
          name={`filters.${index}.columnName`}
          label="View Column Name"
          text="The column in the report view"
          type="select"
          options={values.reportView && values.reportView.columns}
          required
        />
      </Col>

      <Col sm>
        <Field
          name={`filters.${index}.type`}
          label="Filter Type"
          type="select"
          options={filterTypeOptions}
          text="if provider or user, column in report view must be display_name"
          required
        />
      </Col>

      {values.filters[index].type && (<>
        {values.filters[index].type.value === 'date range' ? (<>
          <Col sm>
            <Field
              name={`filters.${index}.startDateLabel`}
              label="Label for start date filter"
              required
            />
          </Col>

          <Col sm>
            <Field
              name={`filters.${index}.endDateLabel`}
              label="Label for end date filter"
              required
            />
          </Col>
        </>) : (
          <Col sm>
            <Field
              name={`filters.${index}.label`}
              label="Filter Label"
              required
            />
          </Col>
        )}

        {values.filters[index].type.value === 'number' && (<>
          <Col sm>
            <Field
              name={`filters.${index}.comparisonType`}
              label="Comparison Type"
              type="select"
              options={numberFilterComparisonTypes}
              text={values.filters[index].comparisonType ? values.filters[index].comparisonType.text : ''}
              required
            />
          </Col>

          <Col sm>
            <Field
              name={`filters.${index}.allowDecimals`}
              label="Allow decimals?"
              type="checkbox"
              text="Note that this will only work if the column itself allows decimals. If the column is only integers, decimal input will cause the report to break."
            />
          </Col>
        </>)}

        {values.filters[index].type.value === 'provider' && (
          <Col sm>
            <Field
              name={`filters.${index}.reviewersOnly`}
              label="Reviewers only?"
              type="checkbox"
            />
          </Col>
        )}

        {values.filters[index].type.value === 'other reference data' && (<>
          <Col sm>
            <Field
              name={`filters.${index}.refDataModel`}
              label="Reference Data Model"
              type="select"
              options={availableRefDataModels}
              required
            />
          </Col>

          {values.filters[index].refDataModel && (
            <Col sm>
              <Field
                name={`filters.${index}.refDataType`}
                label="Reference Data Type"
                type="select"
                options={values.filters[index].refDataModel.availableTypes}
                text="type of reference data"
              />
            </Col>
          )}
        </>)}

        {values.filters[index].type.value === 'boolean radio buttons' && (<>
          <Col sm>
            <Field
              name={`filters.${index}.radioButtonTreatNullAsFalse`}
              label="Treat null as false"
              type="checkbox"
            />
          </Col>

          <Col sm>
            <Field
              name={`filters.${index}.radioButtonOptionAll`}
              label="Label of 'All' option"
            />
          </Col>

          <Col sm>
            <Field
              name={`filters.${index}.radioButtonOptionTrue`}
              label="Label of 'True' option"
            />
          </Col>

          <Col sm>
            <Field
              name={`filters.${index}.radioButtonOptionFalse`}
              label="Label of 'False' option"
            />
          </Col>
        </>)}

        {values.filters[index].type.value === 'custom dropdown' && (<>
          <Col sm>
            <Field
              name={`filters.${index}.customDropdownOptions`}
              label="Options"
              type="select"
              options={values.filters[index].customDropdownOptions}
              noOptionsMessage={() => 'Begin typing to add an option'}
              creatable
              multi
              onCreateOption={(inputValue) => {
                const newOption = {
                  label: inputValue.trim(),
                  value: inputValue.trim(),
                };

                setFieldValue(`filters.${index}.customDropdownOptions`, [...values.filters[index].customDropdownOptions, newOption]);
              }}
            />
          </Col>

          <Col sm>
            <Field
              name={`filters.${index}.isCaseInsensitive`}
              label="Options are case insensitive"
              type="checkbox"
              text="If this is checked, an option labeled 'Sam' will match on 'Sam' or 'sam'. If this is not checked, 'Sam' will only match 'Sam'."
            />
          </Col>
        </>)}

        {['user', 'provider', 'facility', 'other reference data', 'custom dropdown'].includes(values.filters[index].type.value) && (
          <Col sm>
            <Field
              name={`filters.${index}.isMultiSelect`}
              label="Multi select?"
              type="checkbox"
            />
          </Col>
        )}

        <DefaultValueField
          filter={values.filters[index]}
          index={index}
        />
      </>)}
    </Row>
  </>);
};

export default FilterForm;
